/**
 * Runtime configuration.
 *
 * The apps used to derive the relay URL from `location.host`, which works only
 * while one origin serves both the UI and the relay. Once each app has its own
 * hostname that assumption breaks, so each deployment drops a `config.json`
 * next to the app.
 *
 * A missing file is not an error: it falls back to same-origin, which keeps the
 * single-host and tailnet deployments working untouched.
 */

const SAME_ORIGIN_RELAY = () =>
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`;

let cached = null;

export async function loadConfig() {
  if (cached) return cached;
  let file = {};
  try {
    const res = await fetch('config.json', { cache: 'no-store' });
    if (res.ok) file = await res.json();
  } catch {
    // No config deployed; same-origin defaults apply.
  }

  const relays = file.relays?.length ? file.relays
    : file.relay ? [file.relay]
    : [SAME_ORIGIN_RELAY()];

  cached = {
    relays,
    /**
     * Where to resolve `/.well-known/herald.json` for domain binding.
     *
     * `null` means the real thing: fetch it from the domain the sender claims,
     * over HTTPS. A non-null value is a development override for deployments
     * where the relay itself serves the document, and production must not use
     * it -- so it is only honoured when explicitly present in config.json.
     */
    wellKnownOrigin: file.wellKnownOrigin ?? null,
    label: file.label ?? null,
  };
  return cached;
}
